import { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { toast } from "sonner";
import {
  Send,
  Plus,
  Trash2,
  Loader2,
  Bot,
  User,
  ExternalLink,
  X,
  Zap,
  CheckCircle,
  XCircle,
  AlertCircle,
  Paperclip,
  Image,
  File,
  Search,
  Database,
  ArrowRight,
  ChevronRight,
  MessageSquare,
  Sparkles,
  Globe,
  Settings2,
  History,
  Brain,
  BarChart3,
  TrendingUp,
  AlertTriangle,
  ClipboardList,
  Mic,
  MicOff,
} from "lucide-react";
import Markdown from "react-markdown";
import { API } from "@/lib/constants";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { ScrumBriefPanel } from "@/components/ScrumPanel";
import PlanReview from "@/components/PlanReview";

// --- Streaming message parser ---
function parseSSE(text) {
  const events = [];
  const lines = text.split("\n");
  let current = {};
  for (const line of lines) {
    if (line.startsWith("event: ")) {
      current.event = line.slice(7).trim();
    } else if (line.startsWith("data: ")) {
      try {
        current.data = JSON.parse(line.slice(6));
      } catch {
        current.data = line.slice(6);
      }
    } else if (line.startsWith(":")) {
      // SSE comment (e.g., keepalive) — ignore
      continue;
    } else if (line === "" && current.event) {
      events.push({ ...current });
      current = {};
    }
  }
  // Flush any remaining complete event that wasn't terminated by a blank line
  // (happens when the stream ends mid-buffer or the last chunk has no trailing \n\n)
  if (current.event && current.data !== undefined) {
    events.push({ ...current });
  }
  return events;
}

// --- Thinking step indicator ---
function ThinkingSteps({ steps }) {
  if (!steps || steps.length === 0) return null;
  return (
    <div className="flex gap-3" data-testid="thinking-steps">
      <div className="w-8 h-8 rounded-full bg-blue-600 flex items-center justify-center flex-shrink-0 shadow-sm">
        <Bot className="w-4 h-4 text-white" />
      </div>
      <div className="flex-1 bg-slate-50 border border-slate-200 rounded-xl p-3 space-y-2 max-w-[75%]">
        {steps.map((step, i) => (
          <div key={i} className="flex items-center gap-2 text-xs text-slate-500 animate-in fade-in duration-300">
            {step.type === "status" && <Sparkles className="w-3.5 h-3.5 text-blue-500 flex-shrink-0" />}
            {step.type === "tool_call" && <Search className="w-3.5 h-3.5 text-amber-500 flex-shrink-0" />}
            {step.type === "tool_result" && <Database className="w-3.5 h-3.5 text-emerald-500 flex-shrink-0" />}
            <span className={i === steps.length - 1 ? "text-slate-700 font-medium" : "text-slate-400"}>
              {step.message}
            </span>
            {i === steps.length - 1 && (
              <Loader2 className="w-3 h-3 animate-spin text-blue-500 ml-1" />
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

// --- Message bubble ---
function MessageBubble({ msg, isLast, pendingAction, sending, onConfirm, onCancel, onNavigate, onSuggestion, overrideJustification, setOverrideJustification, user }) {
  const isUser = msg.role === "user";

  return (
    <div className={`flex gap-3 ${isUser ? "justify-end" : ""}`} data-testid={`message-${msg.role}`}>
      {!isUser && (
        <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 shadow-sm ${
          msg.type === "confirmation" ? "bg-amber-500" :
          msg.type === "success" ? "bg-emerald-500" :
          msg.type === "error" ? "bg-red-500" :
          "bg-blue-600"
        }`}>
          {msg.type === "confirmation" ? <Zap className="w-4 h-4 text-white" /> :
           msg.type === "success" ? <CheckCircle className="w-4 h-4 text-white" /> :
           msg.type === "error" ? <XCircle className="w-4 h-4 text-white" /> :
           <Bot className="w-4 h-4 text-white" />}
        </div>
      )}
      <div className={`max-w-[75%] rounded-xl px-4 py-3 ${
        isUser
          ? "bg-blue-600 text-white shadow-sm"
          : msg.type === "confirmation"
          ? "bg-amber-50 text-slate-800 border border-amber-200 shadow-sm"
          : msg.type === "success"
          ? "bg-emerald-50 text-slate-800 border border-emerald-200 shadow-sm"
          : msg.type === "error"
          ? "bg-red-50 text-slate-800 border border-red-200 shadow-sm"
          : "bg-white text-slate-800 border border-slate-200 shadow-sm"
      }`}>
        {msg.file && (
          <div className="flex items-center gap-1.5 mb-2 text-xs opacity-80">
            {["png", "jpg", "jpeg", "webp", "gif", "heic"].includes(msg.file.file_type) ? (
              <Image className="w-3 h-3" />
            ) : (
              <File className="w-3 h-3" />
            )}
            <span>{msg.file.filename}</span>
          </div>
        )}
        {isUser ? (
          <div className="text-sm text-white">
            <Markdown
              components={{
                p: ({children}) => <p className="mb-2 last:mb-0 leading-relaxed text-white">{children}</p>,
                a: ({children, href}) => <a href={href} className="text-blue-200 underline" target="_blank" rel="noopener noreferrer">{children}</a>,
              }}
            >{msg.content}</Markdown>
          </div>
        ) : (
          <div className="prose prose-sm prose-slate max-w-none [&>p]:mb-2 [&>p:last-child]:mb-0 [&>ul]:mb-2 [&>ol]:mb-2 [&>h1]:text-base [&>h2]:text-sm [&>h3]:text-sm [&>*]:leading-relaxed text-sm">
            <Markdown>{msg.content}</Markdown>
          </div>
        )}

        {/* Confirmation UI */}
        {msg.type === "confirmation" && pendingAction && isLast && (
          <div className="mt-3 pt-3 border-t border-amber-200/60">
            {msg.risk_assessment && msg.risk_assessment.level !== "LOW" && (
              <div data-testid="risk-badge" className={`mb-3 px-3 py-2 rounded-lg text-xs ${
                msg.risk_assessment.level === "HIGH" ? "bg-red-50 border border-red-200" : "bg-amber-50 border border-amber-200"
              }`}>
                <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold tracking-wide uppercase ${
                  msg.risk_assessment.level === "HIGH" ? "bg-red-100 text-red-700" : "bg-amber-100 text-amber-700"
                }`}>
                  <AlertCircle className="w-3 h-3" />
                  {msg.risk_assessment.level} RISK
                </span>
              </div>
            )}
            {msg.risk_assessment?.level === "HIGH" && (
              <div className="mb-3">
                <label className="text-xs font-medium text-red-700 block mb-1">Justification required:</label>
                <textarea
                  data-testid="override-justification"
                  className="w-full text-xs border border-red-200 rounded-md p-2 bg-white focus:outline-none focus:ring-1 focus:ring-red-400"
                  rows={2}
                  placeholder="Explain why you are proceeding despite HIGH risk..."
                  value={overrideJustification}
                  onChange={(e) => setOverrideJustification(e.target.value)}
                />
              </div>
            )}
            <div className="flex gap-2 flex-wrap">
              <Button
                data-testid="confirm-action-btn"
                size="sm"
                onClick={onConfirm}
                disabled={sending || (msg.risk_assessment?.level === "HIGH" && !overrideJustification?.trim())}
                className={msg.risk_assessment?.level === "HIGH"
                  ? "bg-red-500 hover:bg-red-600 text-white"
                  : "bg-blue-600 hover:bg-blue-700 text-white"
                }
              >
                {sending ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <CheckCircle className="w-3 h-3 mr-1" />}
                {msg.risk_assessment?.level === "HIGH" ? "Override" : "Confirm"}
              </Button>
              {msg.risk_assessment?.refinement_options?.map((opt, oidx) => (
                <Button key={oidx} data-testid={`refinement-option-${oidx}`} size="sm" variant="outline" className="text-xs" onClick={() => { onCancel(); }}>
                  {opt.label}
                </Button>
              ))}
              <Button data-testid="cancel-action-btn" size="sm" variant="outline" onClick={onCancel} disabled={sending}>
                Cancel
              </Button>
            </div>
          </div>
        )}

        {/* Navigation link */}
        {msg.redirect_url && !msg.plan_id && (
          <Button size="sm" variant="outline" className="mt-2 gap-1" onClick={() => onNavigate(msg.redirect_url)}>
            <ExternalLink className="w-3 h-3" /> View
          </Button>
        )}

        {/* Multi-action plan review panel (B2: reviewable table inside Ask Rex) */}
        {msg.plan_id && (
          <div className="mt-3 border-t border-slate-200/60 pt-3" data-testid={`inline-plan-${msg.plan_id}`}>
            <PlanReview planId={msg.plan_id} user={user} />
            <button onClick={() => onNavigate(`/plans/${msg.plan_id}`)} className="mt-2 text-[11px] text-slate-500 hover:underline">Open in Action plans</button>
          </div>
        )}

        {/* Suggestions */}
        {msg.suggestions && msg.suggestions.length > 0 && (
          <div className="mt-3 pt-3 border-t border-slate-200/60">
            <p className="text-xs text-slate-400 mb-2">Next steps:</p>
            <div className="flex flex-wrap gap-2">
              {msg.suggestions.map((s, i) => (
                <Button key={i} size="sm" variant="outline" className="text-xs" onClick={() => onSuggestion(s)} disabled={sending}>
                  {s.label}
                </Button>
              ))}
            </div>
          </div>
        )}

        {/* Evidence badge */}
        {!isUser && msg.evidence && (
          <div className="mt-3 pt-2 border-t border-slate-100">
            <div className="flex items-center gap-2 flex-wrap">
              <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold tracking-wide uppercase ${
                msg.evidence.level === "VERIFIED" ? "bg-emerald-100 text-emerald-700" :
                msg.evidence.level === "SUPPORTED" ? "bg-amber-100 text-amber-700" :
                "bg-red-100 text-red-700"
              }`}>
                {msg.evidence.level === "VERIFIED" ? <CheckCircle className="w-3 h-3" /> :
                 msg.evidence.level === "SUPPORTED" ? <AlertCircle className="w-3 h-3" /> :
                 <XCircle className="w-3 h-3" />}
                {msg.evidence.level}
              </span>
              {msg.evidence.sources?.length > 0 && (
                <span className="text-[10px] text-slate-400">
                  {msg.evidence.sources.slice(0, 3).join(", ")}
                </span>
              )}
            </div>
          </div>
        )}

        <p className={`text-[10px] mt-1.5 ${isUser ? "text-blue-200" : "text-slate-300"}`}>
          {msg.timestamp ? new Date(msg.timestamp).toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" }) : ""}
        </p>
      </div>
      {isUser && (
        <div className="w-8 h-8 rounded-full bg-slate-700 flex items-center justify-center flex-shrink-0 shadow-sm">
          <User className="w-4 h-4 text-white" />
        </div>
      )}
    </div>
  );
}

// --- Suggested prompts ---
const SUGGESTED_PROMPTS = [
  { icon: Search, label: "What's in my pipeline?", prompt: "What's in my pipeline?" },
  { icon: ArrowRight, label: "What should I do next?", prompt: "What should I do next with my top client?" },
  { icon: Database, label: "Show pipeline breakdown", prompt: "Show me pipeline analytics for this month" },
  { icon: Sparkles, label: "Draft a proposal", prompt: "Create a proposal for " },
];

export default function AskRex({ user }) {
  const navigate = useNavigate();
  const location = useLocation();

  const [sessions, setSessions] = useState([]);
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState("");
  const [sending, setSending] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [thinkingSteps, setThinkingSteps] = useState([]);
  const [streamingText, setStreamingText] = useState("");

  const [pendingAction, setPendingAction] = useState(null);
  const [overrideJustification, setOverrideJustification] = useState("");
  const [agentSessionId, setAgentSessionId] = useState(() => {
    const stored = localStorage.getItem("rex_agent_session_id");
    if (stored) return stored;
    const newId = `agent_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
    localStorage.setItem("rex_agent_session_id", newId);
    return newId;
  });

  const [uploadedFile, setUploadedFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [memoryStats, setMemoryStats] = useState(null);
  const [patternInsights, setPatternInsights] = useState(null);
  const [nudges, setNudges] = useState(null);
  const [showInsights, setShowInsights] = useState(false);
  const [showScrum, setShowScrum] = useState(false);
  const fileInputRef = useRef(null);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  // Voice recognition
  const [isListening, setIsListening] = useState(false);
  const [voiceSupported, setVoiceSupported] = useState(false);
  const recognitionRef = useRef(null);

  useEffect(() => {
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (SpeechRecognition) {
      setVoiceSupported(true);
      const recognition = new SpeechRecognition();
      recognition.continuous = true;
      recognition.interimResults = true;
      recognition.lang = "en-GB";

      let finalTranscript = "";

      recognition.onresult = (event) => {
        let interim = "";
        for (let i = event.resultIndex; i < event.results.length; i++) {
          const t = event.results[i][0].transcript;
          if (event.results[i].isFinal) {
            finalTranscript += t + " ";
          } else {
            interim = t;
          }
        }
        setInputMessage(finalTranscript + interim);
      };

      recognition.onerror = (event) => {
        if (event.error !== "aborted") {
          toast.error(`Voice error: ${event.error}`);
        }
        setIsListening(false);
        finalTranscript = "";
      };

      recognition.onend = () => {
        setIsListening(false);
        finalTranscript = "";
      };

      recognitionRef.current = recognition;
    }
    return () => { recognitionRef.current?.abort(); };
  }, []);

  const toggleVoice = () => {
    if (!recognitionRef.current) return;
    if (isListening) {
      recognitionRef.current.stop();
      setIsListening(false);
      inputRef.current?.focus();
    } else {
      setInputMessage("");
      recognitionRef.current.start();
      setIsListening(true);
    }
  };

  useEffect(() => { fetchSessions(); fetchMemoryStats(); fetchPatternInsights(); fetchNudges(); loadCurrentSession(); }, []);
  useEffect(() => { messagesEndRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages, thinkingSteps]);

  // Handle prefilled prompt
  const prefillHandled = useRef(false);
  useEffect(() => {
    if (location.state?.prefillPrompt && !prefillHandled.current) {
      prefillHandled.current = true;
      const prompt = location.state.prefillPrompt;
      window.history.replaceState({}, document.title);
      setTimeout(() => {
        setInputMessage(prompt);
        sendMessageWithText(prompt);
      }, 300);
    }
  }, [location.state]);

  const fetchSessions = async () => {
    try {
      const response = await fetch(`${API}/ask-rex/sessions`, { credentials: "include" });
      if (response.ok) setSessions(await response.json());
    } catch (e) { console.error("Error fetching sessions:", e); }
  };

  const fetchMemoryStats = async () => {
    try {
      const response = await fetch(`${API}/ask-rex/memory-stats`, { credentials: "include" });
      if (response.ok) setMemoryStats(await response.json());
    } catch (e) { console.error("Error fetching memory stats:", e); }
  };

  const fetchPatternInsights = async () => {
    try {
      const response = await fetch(`${API}/ask-rex/patterns`, { credentials: "include" });
      if (response.ok) setPatternInsights(await response.json());
    } catch (e) { console.error("Error fetching patterns:", e); }
  };

  const fetchNudges = async () => {
    try {
      const response = await fetch(`${API}/ask-rex/nudges`, { credentials: "include" });
      if (response.ok) setNudges(await response.json());
    } catch (e) { console.error("Error fetching nudges:", e); }
  };

  const loadCurrentSession = async () => {
    try {
      const sid = localStorage.getItem("rex_agent_session_id");
      if (!sid) return;
      const response = await fetch(`${API}/ask-rex/sessions/${sid}`, { credentials: "include" });
      if (response.ok) {
        const session = await response.json();
        if (session && session.messages && session.messages.length > 0) {
          setMessages(session.messages.map(m => ({
            role: m.role,
            content: m.content,
            timestamp: m.timestamp || session.updated_at,
          })));
        }
      }
    } catch (e) { console.error("Error loading session:", e); }
  };

  const loadSession = async (sessionId) => {
    try {
      const response = await fetch(`${API}/ask-rex/sessions/${sessionId}`, { credentials: "include" });
      if (response.ok) {
        const session = await response.json();
        if (session && session.messages) {
          setAgentSessionId(sessionId);
          localStorage.setItem("rex_agent_session_id", sessionId);
          setMessages(session.messages.map(m => ({
            role: m.role,
            content: m.content,
            timestamp: m.timestamp || session.updated_at,
          })));
          setPendingAction(null);
          setThinkingSteps([]);
          setShowHistory(false);
        }
      }
    } catch (e) { console.error("Error loading session:", e); }
  };

  const startNewChat = useCallback(() => {
    setMessages([]);
    setPendingAction(null);
    setThinkingSteps([]);
    const newId = `agent_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
    setAgentSessionId(newId);
    localStorage.setItem("rex_agent_session_id", newId);
    inputRef.current?.focus();
  }, []);

  const deleteSession = async (sessionId, e) => {
    e.stopPropagation();
    try {
      const response = await fetch(`${API}/ask-rex/sessions/${sessionId}`, { method: "DELETE", credentials: "include" });
      if (response.ok) {
        setSessions(prev => prev.filter(s => s.session_id !== sessionId));
        toast.success("Conversation deleted");
      }
    } catch { toast.error("Failed to delete"); }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 20 * 1024 * 1024) { toast.error("File too large. Max 20MB."); return; }
    const allowed = ["pdf", "docx", "xlsx", "xls", "png", "jpg", "jpeg", "webp", "heic", "gif", "txt", "csv", "md", "json", "xml", "html", "eml"];
    const ext = file.name.split(".").pop()?.toLowerCase();
    if (!allowed.includes(ext)) { toast.error(`Unsupported file type: .${ext}`); return; }

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      const res = await fetch(`${API}/ask-rex/upload`, { method: "POST", credentials: "include", body: formData });
      if (!res.ok) { toast.error((await res.json()).detail || "Upload failed"); return; }
      const data = await res.json();
      setUploadedFile(data);
      toast.success(`${file.name} ready`);
      inputRef.current?.focus();
    } catch (err) { toast.error("Upload failed: " + err.message); }
    finally { setUploading(false); if (fileInputRef.current) fileInputRef.current.value = ""; }
  };

  // --- Core send with streaming ---
  const sendMessageWithText = async (text) => {
    if (sending) return;
    const userMessage = text.trim() || (uploadedFile ? `Analyse this file: ${uploadedFile.filename}` : "");
    if (!userMessage) return;

    setInputMessage("");
    setSending(true);
    setThinkingSteps([]);
    // Clear any stale pending confirmation — the backend will also clear it on new instruction
    if (pendingAction) setPendingAction(null);

    const tempUserMsg = {
      role: "user",
      content: userMessage,
      timestamp: new Date().toISOString(),
      file: uploadedFile ? { filename: uploadedFile.filename, file_type: uploadedFile.file_type } : null,
    };
    setMessages(prev => [...prev, tempUserMsg]);

    const currentUpload = uploadedFile;
    setUploadedFile(null);

    const conversationHistory = messages.slice(-10).map(m => ({ role: m.role, content: m.content }));

    try {
      const response = await fetch(`${API}/ask-rex/agent/stream`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          instruction: userMessage,
          session_id: agentSessionId,
          conversation_history: conversationHistory,
          upload_id: currentUpload?.upload_id || null,
        }),
      });

      if (!response.ok) {
        // Fallback: try non-streaming endpoint
        const fallbackRes = await fetch(`${API}/ask-rex/agent`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify({
            instruction: userMessage,
            session_id: agentSessionId,
            conversation_history: conversationHistory,
            upload_id: currentUpload?.upload_id || null,
          }),
        });
        if (fallbackRes.ok) {
          handleAgentResponse(await fallbackRes.json());
        } else {
          toast.error("Failed to get response from Rex");
          setMessages(prev => prev.slice(0, -1));
        }
        return;
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      let responseHandled = false;

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const events = parseSSE(buffer);

        // Keep any partial event at the end (incomplete — no trailing \n\n)
        const lastNewline = buffer.lastIndexOf("\n\n");
        if (lastNewline !== -1) {
          buffer = buffer.slice(lastNewline + 2);
        }

        for (const evt of events) {
          if (evt.event === "status") {
            const msg = evt.data.message || (evt.data.step === "classified" ? "Understanding your request..." : `${evt.data.step}...`);
            setThinkingSteps(prev => [...prev, { type: "status", message: msg }]);
          } else if (evt.event === "token") {
            // Genuine token stream of the answer as it is written.
            setThinkingSteps([]);
            setStreamingText(prev => prev + (evt.data.delta || ""));
          } else if (evt.event === "tool_call") {
            // A tool ran — any preamble streamed so far was not the final answer.
            setStreamingText("");
            setThinkingSteps(prev => [...prev, { type: "tool_call", message: evt.data.message || `Calling ${evt.data.tool}...` }]);
          } else if (evt.event === "tool_result") {
            setThinkingSteps(prev => [...prev, { type: "tool_result", message: evt.data.summary ? `Got: ${evt.data.summary.slice(0, 100)}` : "Done" }]);
          } else if (evt.event === "response") {
            setThinkingSteps([]);
            setStreamingText("");
            handleAgentResponse(evt.data);
            responseHandled = true;
          } else if (evt.event === "done") {
            setThinkingSteps([]);
            setStreamingText("");
            // Only handle done if no response was emitted yet (fallback)
            if (!responseHandled) {
              handleAgentResponse(evt.data);
            }
            responseHandled = true;
          } else if (evt.event === "confirmation") {
            setThinkingSteps([]);
            setStreamingText("");
            handleAgentResponse(evt.data);
            responseHandled = true;
          } else if (evt.event === "error") {
            setThinkingSteps([]);
            toast.error(evt.data.error || "Something went wrong");
          } else if (evt.event === "correction_learned") {
            // Hardening 2: Inline toast for learned standards
            toast.success(evt.data.message || "Standard learned", {
              duration: 4000,
              style: { background: "#f0f9ff", border: "1px solid #0ea5e9", color: "#0369a1" },
            });
          } else if (evt.event === "bypass_suggested") {
            // Phase 4: Auto-suggest bypass capture
            toast(evt.data.message || "Bypass detected — log reason?", {
              duration: 8000,
              action: {
                label: "Log Bypass",
                onClick: () => {
                  setInputMessage("log this as a bypass because of ");
                  inputRef.current?.focus();
                },
              },
              style: { background: "#fef3c7", border: "1px solid #f59e0b", color: "#92400e" },
            });
          }
        }

        // Bug fix (May 19, 2026): once we have a terminal event (response,
        // confirmation, or done), stop waiting on the stream. The server's
        // background work (session-persist, learning, etc.) keeps the
        // connection open after emitting the answer; if any of that work
        // hangs (slow DB write, stuck tool, cleanup deadlock), the client
        // would otherwise wait forever and lock the input. Cancelling the
        // reader unblocks the UI immediately — server-side cleanup still
        // runs in its own finally.
        if (responseHandled) {
          try { await reader.cancel(); } catch (_) { /* ignore */ }
          break;
        }
      }

      // Process any remaining buffer content after stream ends
      if (buffer.trim()) {
        const finalEvents = parseSSE(buffer);
        for (const evt of finalEvents) {
          if (evt.event === "response" || evt.event === "done") {
            setThinkingSteps([]);
            if (!responseHandled) {
              handleAgentResponse(evt.data);
              responseHandled = true;
            }
          } else if (evt.event === "confirmation") {
            setThinkingSteps([]);
            if (!responseHandled) {
              handleAgentResponse(evt.data);
              responseHandled = true;
            }
          } else if (evt.event === "error") {
            setThinkingSteps([]);
            toast.error(evt.data?.error || "Something went wrong");
          }
        }
      }
    } catch (error) {
      toast.error("Failed to connect to Rex");
      setMessages(prev => prev.slice(0, -1));
    } finally {
      setSending(false);
      setThinkingSteps([]);
      setStreamingText("");
      // Refresh memory stats in the background (Rex may have learned something)
      setTimeout(() => { fetchMemoryStats(); fetchSessions(); }, 3000);
    }
  };

  const sendMessage = () => {
    if (isListening) {
      recognitionRef.current?.stop();
      setIsListening(false);
    }
    sendMessageWithText(inputMessage);
  };

  const handleAgentResponse = (data) => {
    if (!data) return;
    const action = data.action;

    // Multi-action plan proposed: render the review panel inline (B2).
    if (data.plan_id) {
      setMessages(prev => [...prev, {
        role: "assistant",
        content: data.response || data.message || "I have prepared a plan for you to review below. Nothing is written until you approve it.",
        timestamp: new Date().toISOString(),
        type: "plan", plan_id: data.plan_id, redirect_url: `/plans/${data.plan_id}`,
      }]);
      return;
    }

    if (data.auto_execute && data.execution_result) {
      const result = data.execution_result;
      const content = data.response || result?.message || (result ? JSON.stringify(result, null, 2) : "Done.");
      setMessages(prev => [...prev, {
        role: "assistant", content: content || "Action completed.",
        timestamp: new Date().toISOString(), type: result?.success ? "success" : "query_result"
      }]);
      return;
    }

    if (action?.startsWith("QUERY_") && data.response) {
      setMessages(prev => [...prev, { role: "assistant", content: data.response, timestamp: new Date().toISOString(), type: "query" }]);
      return;
    }

    if (action === "NAVIGATE" && data.execution_result?.redirect_url) {
      setMessages(prev => [...prev, {
        role: "assistant", content: data.response || data.execution_result.message || "Navigating...",
        timestamp: new Date().toISOString(), type: "success", redirect_url: data.execution_result.redirect_url
      }]);
      navigate(data.execution_result.redirect_url);
      return;
    }

    if (action === "RESPOND" || action === "ERROR") {
      setMessages(prev => [...prev, {
        role: "assistant",
        content: data.response || data.error || "I'm not sure how to help with that.",
        timestamp: new Date().toISOString(),
        evidence: data.evidence || null,
        classification: data.classification || null,
      }]);
      return;
    }

    if (action === "CLARIFY") {
      const questions = data.questions?.join("\n") || data.message || "Could you provide more details?";
      setMessages(prev => [...prev, { role: "assistant", content: questions, timestamp: new Date().toISOString() }]);
      return;
    }

    if (action === "GET_CLIENT_INSIGHTS") {
      setMessages(prev => [...prev, { role: "assistant", content: data.insights || data.response || "Here's what I found.", timestamp: new Date().toISOString(), type: "insights" }]);
      return;
    }

    if (data.requires_confirmation || !data.auto_execute) {
      // SYSTEM-WIDE INVARIANT: never render an approve/reject gate when there is
      // no visible proposal to approve. A confirmation gate requires either a
      // real confirmation message OR concrete parameters. Otherwise render the
      // available text as a plain assistant message (defends against backend
      // misclassification that asks to confirm an "invisible" action).
      const params = data.params || data.parameters || {};
      const hasProposal = Boolean(
        (data.confirmation_message && String(data.confirmation_message).trim()) ||
        (params && Object.keys(params).length > 0)
      );
      if (!hasProposal) {
        const text = [data.confirmation_message, data.message, data.response]
          .find(v => typeof v === "string" && v.trim() && v.trim().toLowerCase() !== "undefined")
          || "I've processed your request.";
        setMessages(prev => [...prev, {
          role: "assistant", content: text, timestamp: new Date().toISOString(),
          evidence: data.evidence || null, classification: data.classification || null,
        }]);
        return;
      }
      const riskAssessment = data.risk_assessment || null;
      setPendingAction({
        action, parameters: params,
        session_id: agentSessionId, task_id: data.task_id || null, risk_assessment: riskAssessment,
      });
      const actionLabel = action ? String(action).replace(/_/g, " ").toLowerCase() : null;
      setMessages(prev => [...prev, {
        role: "assistant",
        content: data.confirmation_message || data.message || (
          actionLabel
            ? `I'm ready to ${actionLabel}. Shall I proceed?`
            : "Shall I proceed with this action?"
        ),
        timestamp: new Date().toISOString(), type: "confirmation", action, parameters: params,
        risk_assessment: riskAssessment,
      }]);
      return;
    }

    if (data.execution_result) {
      const result = data.execution_result;
      const successMsg = result.message || data.response || "Done.";
      const errorMsg = result.error || data.response || "Something went wrong.";
      const safeErr = (typeof errorMsg === "string" && errorMsg !== "undefined") ? errorMsg : "Something went wrong.";
      setMessages(prev => [...prev, {
        role: "assistant",
        content: result.success ? successMsg : `Issue: ${safeErr}`,
        timestamp: new Date().toISOString(),
        type: result.success ? "success" : "error",
        redirect_url: result.redirect_url,
        suggestions: result.suggestions || [],
      }]);
      if (result.redirect_url) toast.success(result.message || "Navigating...");
      return;
    }

    // Fallback — drop literal "undefined" strings so the user never sees them
    const fallback = [data.message, data.confirmation_message, data.response]
      .find(v => typeof v === "string" && v.trim() && v.trim().toLowerCase() !== "undefined")
      || "I've processed your request.";
    setMessages(prev => [...prev, {
      role: "assistant",
      content: fallback,
      timestamp: new Date().toISOString(),
    }]);
  };

  const executeConfirmedAction = async () => {
    if (!pendingAction) return;
    setSending(true);
    try {
      const response = await fetch(`${API}/ask-rex/agent/execute`, {
        method: "POST", headers: { "Content-Type": "application/json" }, credentials: "include",
        body: JSON.stringify({
          action: pendingAction.action, parameters: pendingAction.parameters,
          session_id: pendingAction.session_id || agentSessionId, task_id: pendingAction.task_id || null,
        }),
      });
      if (response.ok) {
        const result = await response.json();
        const execResult = result.execution_result || result;
        const isSuccess = execResult.success || result.success;
        const pickSafe = (...xs) => xs.find(v => typeof v === "string" && v.trim() && v.trim().toLowerCase() !== "undefined");
        const msg = isSuccess
          ? (pickSafe(result.response, execResult.message) || "Action completed.")
          : (pickSafe(result.response, execResult.error) || "Something went wrong.");
        setMessages(prev => [...prev, {
          role: "assistant", content: msg, timestamp: new Date().toISOString(),
          type: isSuccess ? "success" : "error",
          redirect_url: execResult.redirect_url || result.redirect_url,
          suggestions: execResult.suggestions || result.suggestions || [],
        }]);
        if (isSuccess && execResult.redirect_url) toast.success(execResult.message || msg);
      } else {
        toast.error("Failed to execute action");
      }
    } catch { toast.error("Failed to execute action"); }
    finally { setPendingAction(null); setSending(false); setOverrideJustification(""); }
  };

  const cancelPendingAction = () => {
    // Send cancellation to backend so memory is cleared
    if (pendingAction?.session_id || agentSessionId) {
      fetch(`${API}/ask-rex/agent/stream`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          instruction: "no, cancel that",
          session_id: pendingAction?.session_id || agentSessionId,
        }),
      }).catch(() => {}); // fire-and-forget
    }
    setPendingAction(null);
    setMessages(prev => [...prev, { role: "assistant", content: "No problem! Let me know what else you'd like to do.", timestamp: new Date().toISOString() }]);
  };

  const executeSuggestion = async (suggestion) => {
    setSending(true);
    try {
      const response = await fetch(`${API}/ask-rex/agent/execute`, {
        method: "POST", headers: { "Content-Type": "application/json" }, credentials: "include",
        body: JSON.stringify({ action: suggestion.action, parameters: suggestion.params || {} }),
      });
      if (response.ok) {
        const result = await response.json();
        setMessages(prev => [...prev, {
          role: "assistant",
          content: result.formatted_response || result.message || result.response || "Here's what I found.",
          timestamp: new Date().toISOString(),
          type: result.success ? "success" : "query_result",
          suggestions: result.suggestions || [],
        }]);
      }
    } catch { toast.error("Failed to execute suggestion"); }
    finally { setSending(false); }
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); sendMessage(); }
  };

  return (
    <div className="h-[calc(100vh-8rem)] flex flex-col" data-testid="ask-rex-page">
      {/* Compact header */}
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2.5">
          <div className="w-9 h-9 rounded-lg bg-blue-600 flex items-center justify-center shadow-sm">
            <Bot className="w-5 h-5 text-white" />
          </div>
          <div>
            <h1 className="text-lg font-semibold text-slate-900 leading-tight" style={{ fontFamily: "Manrope, sans-serif" }}>
              Ask Rex
            </h1>
            <p className="text-xs text-slate-400">Commercial intelligence agent</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {memoryStats && memoryStats.total_facts_learned > 0 && (
            <div className="hidden sm:flex items-center gap-1.5 px-2.5 py-1 bg-emerald-50 border border-emerald-200 rounded-full" data-testid="memory-stats-badge" title="Rex's institutional memory — facts learned from interactions">
              <Brain className="w-3.5 h-3.5 text-emerald-600" />
              <span className="text-xs font-medium text-emerald-700">{memoryStats.total_facts_learned} memories</span>
            </div>
          )}
          <Button variant="ghost" size="sm" className="gap-1.5 text-xs text-slate-500 hover:text-slate-700" onClick={() => setShowHistory(!showHistory)} data-testid="toggle-history-btn">
            <History className="w-3.5 h-3.5" />
            <span className="hidden sm:inline">History</span>
          </Button>
          <Button variant="ghost" size="sm" className={`gap-1.5 text-xs ${showInsights ? 'text-blue-600 bg-blue-50' : 'text-slate-500 hover:text-slate-700'} relative`} onClick={() => setShowInsights(!showInsights)} data-testid="toggle-insights-btn">
            <BarChart3 className="w-3.5 h-3.5" />
            <span className="hidden sm:inline">Insights</span>
            {nudges && nudges.by_severity?.critical + nudges.by_severity?.high > 0 && (
              <span className="absolute -top-1 -right-1 w-4 h-4 bg-red-500 text-white text-[9px] font-bold rounded-full flex items-center justify-center">
                {(nudges.by_severity?.critical || 0) + (nudges.by_severity?.high || 0)}
              </span>
            )}
          </Button>
          <Button variant="ghost" size="sm" className={`gap-1.5 text-xs ${showScrum ? 'text-blue-600 bg-blue-50' : 'text-slate-500 hover:text-slate-700'}`} onClick={() => setShowScrum(!showScrum)} data-testid="toggle-scrum-btn">
            <ClipboardList className="w-3.5 h-3.5" />
            <span className="hidden sm:inline">Scrum</span>
          </Button>
          <Button size="sm" className="gap-1.5 bg-blue-600 hover:bg-blue-700 text-white" onClick={startNewChat} data-testid="new-chat-btn">
            <Plus className="w-3.5 h-3.5" />
            <span className="hidden sm:inline">New</span>
          </Button>
        </div>
      </div>

      {/* Main area */}
      <div className="flex-1 flex gap-3 min-h-0 overflow-hidden">
        {/* History panel */}
        {showHistory && (
          <div className="w-64 flex-shrink-0 bg-white rounded-xl border border-slate-200 flex flex-col h-full shadow-sm animate-in slide-in-from-left duration-200" data-testid="history-panel">
            <div className="p-3 border-b border-slate-100">
              <p className="text-xs font-medium text-slate-500 uppercase tracking-wider">Recent Conversations</p>
            </div>
            <ScrollArea className="flex-1 p-2">
              {sessions.length === 0 ? (
                <p className="text-xs text-slate-400 text-center py-6">No conversations yet</p>
              ) : (
                <div className="space-y-0.5">
                  {sessions.map((session) => (
                    <div
                      key={session.session_id}
                      onClick={() => loadSession(session.session_id)}
                      role="button"
                      tabIndex={0}
                      className="w-full text-left px-3 py-2 rounded-lg text-sm transition-colors group flex items-center justify-between cursor-pointer hover:bg-slate-50 text-slate-600"
                      data-testid={`session-${session.session_id}`}
                    >
                      <div className="flex items-center gap-2 min-w-0 flex-1">
                        <MessageSquare className="w-3.5 h-3.5 text-slate-400 flex-shrink-0" />
                        <span className="truncate text-xs">{session.title}</span>
                      </div>
                      <button onClick={(e) => deleteSession(session.session_id, e)} className="opacity-0 group-hover:opacity-100 text-slate-400 hover:text-red-500 transition-opacity">
                        <Trash2 className="w-3 h-3" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </ScrollArea>
          </div>
        )}

        {/* Chat area */}
        <div className="bg-white rounded-xl border border-slate-200 flex flex-col min-h-0 shadow-sm flex-1">

          {/* Insights bar — compact pattern insights strip */}
          {showInsights && !showScrum && patternInsights && (
            <div className="p-3 border-b border-slate-200 bg-slate-50/50 animate-in slide-in-from-top duration-200" data-testid="insights-panel">
              <div className="flex items-center gap-2 mb-2">
                <BarChart3 className="w-3.5 h-3.5 text-blue-600" />
                <span className="text-xs font-semibold text-slate-700 uppercase tracking-wider">Pattern Insights</span>
                <span className="text-[10px] text-slate-400 ml-auto">
                  Data quality: {patternInsights.data_quality?.quality_level || "early"}
                </span>
              </div>
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-2">
                {/* Win Rate */}
                <div className="bg-white rounded-lg border border-slate-200 px-3 py-2">
                  <div className="text-[10px] text-slate-400 uppercase tracking-wider">Win Rate</div>
                  <div className="text-lg font-bold text-slate-800" style={{ fontFamily: "Manrope, sans-serif" }}>
                    {patternInsights.win_loss?.win_rate != null ? `${patternInsights.win_loss.win_rate}%` : "—"}
                  </div>
                  <div className="text-[10px] text-slate-400">
                    {patternInsights.win_loss?.total_won || 0}W / {patternInsights.win_loss?.total_lost || 0}L
                  </div>
                </div>
                {/* Pipeline Shape */}
                <div className="bg-white rounded-lg border border-slate-200 px-3 py-2">
                  <div className="text-[10px] text-slate-400 uppercase tracking-wider">Pipeline</div>
                  <div className="text-lg font-bold text-slate-800 capitalize" style={{ fontFamily: "Manrope, sans-serif" }}>
                    {patternInsights.pipeline_health?.shape || "—"}
                  </div>
                  <div className="text-[10px] text-slate-400">
                    {patternInsights.pipeline_health?.total_clients || 0} clients
                  </div>
                </div>
                {/* Top Insight */}
                {patternInsights.insights?.[0] && (
                  <div className={`bg-white rounded-lg border px-3 py-2 ${
                    patternInsights.insights[0].priority === "HIGH" ? "border-red-200" : "border-amber-200"
                  }`}>
                    <div className="text-[10px] text-slate-400 uppercase tracking-wider flex items-center gap-1">
                      <AlertTriangle className="w-2.5 h-2.5" />
                      Top Insight
                    </div>
                    <div className="text-xs font-medium text-slate-700 mt-0.5 line-clamp-2">
                      {patternInsights.insights[0].title}
                    </div>
                  </div>
                )}
                {/* Stale Deals */}
                <div className="bg-white rounded-lg border border-slate-200 px-3 py-2">
                  <div className="text-[10px] text-slate-400 uppercase tracking-wider">At Risk</div>
                  <div className="text-lg font-bold text-slate-800" style={{ fontFamily: "Manrope, sans-serif" }}>
                    {patternInsights.stale_deals?.filter(d => d.risk_level !== "LOW").length || 0}
                  </div>
                  <div className="text-[10px] text-slate-400">
                    deals cooling off
                  </div>
                </div>
              </div>
              {/* Quick insight pills */}
              {patternInsights.insights?.length > 1 && (
                <div className="flex gap-1.5 mt-2 overflow-x-auto">
                  {patternInsights.insights.slice(1, 5).map((insight, i) => (
                    <button
                      key={i}
                      onClick={() => sendMessageWithText(`Tell me more about: ${insight.title}`)}
                      className={`flex-shrink-0 px-2.5 py-1 rounded-full text-[10px] font-medium border cursor-pointer transition-colors ${
                        insight.priority === "HIGH" ? "bg-red-50 text-red-700 border-red-200 hover:bg-red-100" :
                        insight.priority === "MEDIUM" ? "bg-amber-50 text-amber-700 border-amber-200 hover:bg-amber-100" :
                        "bg-slate-50 text-slate-600 border-slate-200 hover:bg-slate-100"
                      }`}
                    >
                      {insight.title}
                    </button>
                  ))}
                </div>
              )}

              {/* Nudges — proactive alerts */}
              {nudges && nudges.nudges?.length > 0 && (
                <div className="mt-3 pt-3 border-t border-slate-200">
                  <div className="flex items-center gap-1.5 mb-2">
                    <AlertTriangle className="w-3 h-3 text-amber-500" />
                    <span className="text-[10px] font-semibold text-slate-600 uppercase tracking-wider">Nudges</span>
                    <span className="text-[10px] text-slate-400">({nudges.total} total)</span>
                  </div>
                  <div className="space-y-1.5 max-h-32 overflow-y-auto">
                    {nudges.nudges.filter(n => n.severity !== "low").slice(0, 5).map((nudge, i) => (
                      <button
                        key={i}
                        onClick={() => {
                          if (nudge.client_id) {
                            sendMessageWithText(`Coach me on the deal with ${nudge.company_name}. ${nudge.message}`);
                          } else {
                            sendMessageWithText(nudge.message);
                          }
                        }}
                        className="w-full text-left flex items-start gap-2 px-2.5 py-1.5 rounded-md border border-slate-200 hover:bg-slate-50 transition-colors group"
                        data-testid={`nudge-${i}`}
                      >
                        <div className={`w-1.5 h-1.5 rounded-full mt-1.5 flex-shrink-0 ${
                          nudge.severity === "critical" ? "bg-red-500" :
                          nudge.severity === "high" ? "bg-amber-500" :
                          nudge.severity === "medium" ? "bg-yellow-400" :
                          "bg-blue-400"
                        }`} />
                        <div className="flex-1 min-w-0">
                          <p className="text-[11px] text-slate-700 leading-snug truncate">{nudge.message}</p>
                          {nudge.suggestion && (
                            <p className="text-[10px] text-slate-400 truncate mt-0.5">{nudge.suggestion.slice(0, 80)}</p>
                          )}
                        </div>
                        <ChevronRight className="w-3 h-3 text-slate-300 group-hover:text-blue-500 mt-1 flex-shrink-0" />
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
          <ScrollArea className="flex-1 p-4 sm:p-6">
            {messages.length === 0 && !sending ? (
              /* Empty state */
              <div className="h-full flex items-center justify-center">
                <div className="text-center max-w-lg">
                  <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-blue-500 to-blue-700 flex items-center justify-center mx-auto mb-5 shadow-lg">
                    <Bot className="w-8 h-8 text-white" />
                  </div>
                  <h2 className="text-xl font-semibold text-slate-800 mb-2" style={{ fontFamily: "Manrope, sans-serif" }}>
                    What can I help you with?
                  </h2>
                  <p className="text-sm text-slate-500 mb-6 max-w-sm mx-auto">
                    I can look up clients, update your pipeline, draft proposals, and provide strategic guidance.
                  </p>
                  <div className="grid grid-cols-2 gap-2">
                    {SUGGESTED_PROMPTS.map((sp, idx) => (
                      <button
                        key={idx}
                        onClick={() => {
                          if (sp.prompt.endsWith(" ")) {
                            setInputMessage(sp.prompt);
                            inputRef.current?.focus();
                          } else {
                            sendMessageWithText(sp.prompt);
                          }
                        }}
                        className="flex items-center gap-2 px-3 py-2.5 rounded-lg border border-slate-200 text-left text-sm text-slate-600 hover:bg-slate-50 hover:border-slate-300 transition-colors group"
                        data-testid={`suggested-prompt-${idx}`}
                      >
                        <sp.icon className="w-4 h-4 text-blue-500 flex-shrink-0" />
                        <span className="truncate">{sp.label}</span>
                        <ChevronRight className="w-3 h-3 text-slate-300 ml-auto group-hover:text-blue-500 transition-colors" />
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            ) : (
              /* Messages */
              <div className="space-y-4">
                {messages.map((msg, idx) => (
                  <MessageBubble
                    key={idx}
                    msg={msg}
                    isLast={idx === messages.length - 1}
                    pendingAction={pendingAction}
                    sending={sending}
                    onConfirm={executeConfirmedAction}
                    onCancel={cancelPendingAction}
                    onNavigate={(url) => navigate(url)}
                    onSuggestion={executeSuggestion}
                    overrideJustification={overrideJustification}
                    setOverrideJustification={setOverrideJustification}
                    user={user}
                  />
                ))}
                {/* Thinking indicator */}
                {thinkingSteps.length > 0 && <ThinkingSteps steps={thinkingSteps} />}
                {/* Live token stream — the answer as it is written */}
                {streamingText && (
                  <div className="flex gap-3" data-testid="streaming-message">
                    <div className="w-8 h-8 rounded-full bg-blue-600 flex items-center justify-center flex-shrink-0 shadow-sm">
                      <Bot className="w-4 h-4 text-white" />
                    </div>
                    <div className="max-w-[75%] rounded-xl px-4 py-3 bg-white text-slate-800 border border-slate-200 shadow-sm">
                      <div className="prose prose-sm prose-slate max-w-none [&>p]:mb-2 [&>p:last-child]:mb-0 [&>ul]:mb-2 [&>ol]:mb-2 [&>h1]:text-base [&>h2]:text-sm [&>h3]:text-sm [&>*]:leading-relaxed text-sm">
                        <Markdown>{streamingText}</Markdown>
                      </div>
                    </div>
                  </div>
                )}
                {sending && thinkingSteps.length === 0 && !streamingText && (
                  <div className="flex gap-3">
                    <div className="w-8 h-8 rounded-full bg-blue-600 flex items-center justify-center flex-shrink-0 shadow-sm">
                      <Bot className="w-4 h-4 text-white" />
                    </div>
                    <div className="bg-slate-50 border border-slate-200 rounded-xl px-4 py-3">
                      <div className="flex items-center gap-2 text-slate-500">
                        <Loader2 className="w-4 h-4 animate-spin text-blue-500" />
                        <span className="text-sm">Rex is thinking...</span>
                      </div>
                    </div>
                  </div>
                )}
                <div ref={messagesEndRef} />
              </div>
            )}
          </ScrollArea>

          {/* Input area */}
          <div className="p-3 sm:p-4 border-t border-slate-100">
            {uploadedFile && (
              <div className="flex items-center gap-2 mb-2 px-3 py-2 bg-blue-50 border border-blue-200 rounded-lg text-xs" data-testid="file-preview-bar">
                {["png", "jpg", "jpeg", "webp", "gif", "heic"].includes(uploadedFile.file_type) ? <Image className="w-3.5 h-3.5 text-blue-600 flex-shrink-0" /> : <File className="w-3.5 h-3.5 text-blue-600 flex-shrink-0" />}
                <span className="text-blue-800 font-medium truncate">{uploadedFile.filename}</span>
                <span className="text-blue-500">{uploadedFile.text_length > 0 ? `${(uploadedFile.text_length / 1000).toFixed(1)}k chars` : "ready"}</span>
                <button onClick={() => setUploadedFile(null)} className="ml-auto text-blue-400 hover:text-blue-600" data-testid="remove-file-btn">
                  <X className="w-3.5 h-3.5" />
                </button>
              </div>
            )}
            {uploading && (
              <div className="flex items-center gap-2 mb-2 px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg text-xs text-slate-500">
                <Loader2 className="w-3.5 h-3.5 animate-spin" />
                <span>Processing file...</span>
              </div>
            )}
            <div className="flex gap-2 items-center">
              <input type="file" ref={fileInputRef} onChange={handleFileUpload}
                accept=".pdf,.docx,.xlsx,.xls,.png,.jpg,.jpeg,.webp,.heic,.gif,.txt,.csv,.md,.json,.xml,.html,.eml,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel,image/*,text/*"
                className="hidden" data-testid="file-input" />
              <Button variant="ghost" size="sm" onClick={() => fileInputRef.current?.click()}
                disabled={sending || uploading}
                className="px-2 text-slate-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg"
                data-testid="upload-file-btn" title="Upload file">
                {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Paperclip className="w-4 h-4" />}
              </Button>
              {voiceSupported && (
                <Button variant="ghost" size="sm" onClick={toggleVoice}
                  disabled={sending}
                  className={`px-2 rounded-lg transition-colors ${isListening ? "text-red-500 bg-red-50 hover:bg-red-100 hover:text-red-600 animate-pulse" : "text-slate-400 hover:text-blue-600 hover:bg-blue-50"}`}
                  data-testid="voice-input-btn" title={isListening ? "Stop recording" : "Voice input"}>
                  {isListening ? <MicOff className="w-4 h-4" /> : <Mic className="w-4 h-4" />}
                </Button>
              )}
              <div className="flex-1 relative">
                <Input
                  ref={inputRef}
                  value={inputMessage}
                  onChange={(e) => setInputMessage(e.target.value)}
                  onKeyPress={handleKeyPress}
                  placeholder={isListening ? "Listening..." : uploadedFile ? `Tell Rex what to do with ${uploadedFile.filename}...` : "Ask Rex anything..."}
                  disabled={sending}
                  className={`pr-10 text-sm border-slate-200 focus:border-blue-400 focus:ring-blue-400/20 rounded-lg ${isListening ? "border-red-300 bg-red-50/30" : ""}`}
                  data-testid="rex-input"
                />
              </div>
              <Button onClick={sendMessage} disabled={(!inputMessage.trim() && !uploadedFile) || sending}
                className="bg-blue-600 hover:bg-blue-700 text-white rounded-lg shadow-sm px-4"
                size="sm" data-testid="send-btn">
                {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
              </Button>
            </div>
          </div>
        </div>

        {/* Scrum Panel */}
        {showScrum && (
          <div className="w-80 flex-shrink-0 bg-white rounded-xl border border-slate-200 flex flex-col h-full shadow-sm animate-in slide-in-from-right duration-200" data-testid="scrum-panel">
            <div className="p-3 border-b border-slate-100 flex items-center justify-between">
              <div className="flex items-center gap-1.5">
                <ClipboardList className="w-3.5 h-3.5 text-blue-600" />
                <span className="text-xs font-semibold text-slate-700 uppercase tracking-wider">Scrum Board</span>
              </div>
              <button onClick={() => setShowScrum(false)} className="text-slate-400 hover:text-slate-600" data-testid="close-scrum-btn">
                <X className="w-3.5 h-3.5" />
              </button>
            </div>
            <ScrollArea className="flex-1 p-3">
              <ScrumBriefPanel onSendMessage={sendMessageWithText} />
            </ScrollArea>
          </div>
        )}
      </div>
    </div>
  );
}
